// [cursor 2026-06-01] S87-T2 finish-up: typed Ask Harper intent + chat context envelope.
// Provides a structured headline/risk context that renders as a user-side card while
// still feeding Harper a clean, human-readable context block. The legacy
// `fintheon:send-chat-text` plain-text path remains as a fallback.

export type ChatContextKind = "headline" | "risk_signal" | "catalyst";

export interface ChatContext {
  kind: ChatContextKind;
  sourceType: string;
  sourceId: string;
  title: string;
  summary?: string | null;
  source?: string | null;
  ivScore?: number | null;
  publishedAt?: string | null;
  imageUrl?: string | null;
  url?: string | null;
}

export interface AskHarperIntent {
  prompt: string;
  context: ChatContext;
}

export const DEFAULT_HEADLINE_PROMPT =
  "Break this headline down. Is it going to affect this or any future sessions this week?";

export const ASK_HARPER_INTENT_EVENT = "fintheon:ask-harper-intent";
export const SEND_CHAT_TEXT_EVENT = "fintheon:send-chat-text";

// Sentinel marks a leading message segment as a structured context envelope.
// It is intentionally non-printable so it never appears in a normal user message.
const CONTEXT_SENTINEL = "\u0000fin-ctx\u0000";

const KIND_LABEL: Record<ChatContextKind, string> = {
  headline: "Headline",
  risk_signal: "Risk Signal",
  catalyst: "Catalyst",
};

export function labelForKind(kind: ChatContextKind): string {
  return KIND_LABEL[kind] ?? "Context";
}

/** Encode a context envelope as the leading line of a single user text part. */
export function encodeContextMessageText(
  context: ChatContext,
  prompt: string,
): string {
  return `${CONTEXT_SENTINEL}${JSON.stringify(context)}\n${prompt}`;
}

export interface ParsedUserText {
  context: ChatContext | null;
  body: string;
}

/** Split a user message text into its optional context envelope and visible body. */
export function parseUserMessageText(text: string): ParsedUserText {
  if (!text.startsWith(CONTEXT_SENTINEL)) return { context: null, body: text };
  const newlineIndex = text.indexOf("\n");
  const envelope =
    newlineIndex === -1
      ? text.slice(CONTEXT_SENTINEL.length)
      : text.slice(CONTEXT_SENTINEL.length, newlineIndex);
  const body = newlineIndex === -1 ? "" : text.slice(newlineIndex + 1);
  try {
    const context = JSON.parse(envelope) as ChatContext;
    return { context, body };
  } catch {
    return { context: null, body: text };
  }
}

/** Human-readable context block prepended to the message Harper actually receives. */
export function buildHarperContextBlock(context: ChatContext): string {
  const lines: (string | null)[] = [
    `[${labelForKind(context.kind)} Context]`,
    `Title: ${context.title}`,
    context.source ? `Source: ${context.source}` : null,
    typeof context.ivScore === "number"
      ? `IV Score: ${context.ivScore.toFixed(1)} / 10`
      : null,
    context.publishedAt ? `Published: ${context.publishedAt}` : null,
    context.summary ? `Summary: ${context.summary}` : null,
    context.url ? `URL: ${context.url}` : null,
  ];
  return lines.filter((line): line is string => Boolean(line)).join("\n");
}

/**
 * Resolve the text Harper should receive from a raw user text part.
 * Replaces a context envelope with a clean context block + prompt body.
 */
export function resolveHarperText(rawText: string): string {
  const { context, body } = parseUserMessageText(rawText);
  if (!context) return rawText;
  const block = buildHarperContextBlock(context);
  return body ? `${block}\n\n${body}` : block;
}

/** Dispatch a typed Ask Harper intent. */
export function dispatchAskHarperIntent(intent: AskHarperIntent): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(ASK_HARPER_INTENT_EVENT, { detail: intent }),
  );
}

/** Plain-text fallback for surfaces that cannot consume the typed intent. */
export function buildAskHarperFallbackText(intent: AskHarperIntent): string {
  return `${buildHarperContextBlock(intent.context)}\n\n${intent.prompt}`;
}
